import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getSymptomGuides, downloadSymptomPDF, downloadAllSymptomsPDF, recordPageVisit } from "@/lib/api";
import { toast } from "sonner";
import { 
  AlertTriangle,
  Phone,
  CheckCircle,
  Stethoscope,
  MessageCircle,
  Activity,
  Brain,
  Utensils,
  Download,
  FileText,
  Loader2
} from "lucide-react";

const categoryIcons = {
  physical: Activity,
  neurological: Brain,
  nutritional: Utensils
};

export default function SymptomGuide() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [symptoms, setSymptoms] = useState([]);
  const [activeSymptom, setActiveSymptom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    recordPageVisit("symptoms").catch(console.error);
    loadSymptoms();
  }, []);

  const loadSymptoms = async () => {
    try {
      const data = await getSymptomGuides();
      setSymptoms(data.symptoms);
      if (data.symptoms.length > 0) {
        setActiveSymptom(data.symptoms[0].symptom.toLowerCase());
      }
    } catch (error) {
      console.error("Error loading symptoms:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (symptomName) => {
    setDownloading(true);
    try {
      const blob = await downloadSymptomPDF(symptomName);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `symptom_guide_${symptomName.toLowerCase().replace(' ', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAllPDF = async () => {
    setDownloading(true);
    try {
      const blob = await downloadAllSymptomsPDF();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hospicehaven_symptom_guide_complete.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Complete guide downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const selectedSymptom = symptoms.find(s => s.symptom.toLowerCase() === activeSymptom);

  return (
    <div className="min-h-screen" data-testid="symptom-guide-page">
      {/* Hero */}
      <section className="hero-gradient section-padding pb-16">
        <div className="container-max">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="max-w-3xl">
              <p className="caption mb-4">{t('symptomGuide.caregiverTraining', 'Caregiver Training')}</p>
              <h1 className="heading-1 text-foreground mb-6">
                {t('symptomGuide.title', 'Symptom Management Guide')}
              </h1>
              <p className="body-lg text-muted-foreground mb-8">
                {t('symptomGuide.description', 'Comprehensive guidance on managing common hospice symptoms. Learn comfort measures and know exactly when to call your hospice team.')}
              </p>
              <Alert className="alert-warning mb-6" data-testid="symptom-alert">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <AlertTitle className="text-amber-800">{t('common.important', 'Important')}</AlertTitle>
                <AlertDescription className="text-amber-700">
                  {t('symptomGuide.alertMessage', 'When in doubt, always call your hospice team. They are available 24/7 and want to hear from you. Never hesitate to reach out.')}
                </AlertDescription>
              </Alert>
            </div>
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadAllPDF}
                disabled={downloading}
                className="whitespace-nowrap"
                data-testid="download-all-pdf-btn"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {t('symptomGuide.downloadComplete', 'Download Complete Guide (PDF)')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Symptom Content */}
      <section className="section-padding pt-8 bg-muted/30" data-testid="symptom-content-section">
        <div className="container-max">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground mt-2">{t('symptomGuide.loading', 'Loading symptom guides...')}</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Symptom List */}
              <div className="lg:col-span-1">
                <Card className="shadow-soft sticky top-24" data-testid="symptom-list">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-serif flex items-center gap-2">
                      <Stethoscope className="w-5 h-5 text-primary" />
                      {t('symptomGuide.symptoms', 'Symptoms')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {symptoms.map((symptom) => {
                      const CategoryIcon = categoryIcons[symptom.category] || Activity;
                      return (
                        <button
                          key={symptom.symptom}
                          onClick={() => setActiveSymptom(symptom.symptom.toLowerCase())}
                          data-testid={`symptom-btn-${symptom.symptom.toLowerCase().replace(' ', '-')}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                            activeSymptom === symptom.symptom.toLowerCase()
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-muted text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <CategoryIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium text-sm">{symptom.symptom}</span>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>

              {/* Symptom Details */}
              <div className="lg:col-span-3">
                {selectedSymptom && (
                  <div className="space-y-6 animate-fade-in" data-testid="symptom-details">
                    {/* Header */}
                    <Card className="shadow-soft">
                      <CardContent className="p-8">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <Badge variant="outline" className="mb-3">
                              {selectedSymptom.category}
                            </Badge>
                            <h2 className="heading-2 text-foreground">
                              {selectedSymptom.symptom}
                            </h2>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadPDF(selectedSymptom.symptom)}
                            disabled={downloading}
                            data-testid="download-symptom-pdf-btn"
                          >
                            {downloading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <FileText className="w-4 h-4 mr-2" />
                                PDF
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="body-lg text-muted-foreground">
                          {selectedSymptom.description}
                        </p>
                      </CardContent>
                    </Card>

                    {/* Comfort Measures */}
                    <Card className="shadow-soft" data-testid="comfort-measures">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 font-serif text-xl">
                          <CheckCircle className="w-5 h-5 text-primary" />
                          {t('symptomGuide.comfortMeasures', 'Comfort Measures')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {selectedSymptom.comfort_measures.map((measure, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-primary text-sm font-medium">{index + 1}</span>
                              </div>
                              <span className="text-foreground">{measure}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* When to Call */}
                    <Card className="shadow-soft border-amber-200" data-testid="when-to-call">
                      <CardHeader className="bg-amber-50 rounded-t-xl">
                        <CardTitle className="flex items-center gap-2 font-serif text-xl text-amber-800">
                          <Phone className="w-5 h-5" />
                          {t('symptomGuide.whenToCall', 'When to Call the Hospice Team')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <ul className="space-y-3">
                          {selectedSymptom.when_to_call.map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-2" />
                              <span className="text-foreground">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Call Immediately */}
                    <Card className="shadow-soft border-red-200" data-testid="call-immediately">
                      <CardHeader className="bg-red-50 rounded-t-xl">
                        <CardTitle className="flex items-center gap-2 font-serif text-xl text-red-800">
                          <AlertTriangle className="w-5 h-5" />
                          {t('symptomGuide.callImmediately', 'CALL IMMEDIATELY If...')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <Alert className="alert-danger mb-4">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            {t('symptomGuide.emergencyNote', 'These situations require immediate attention. Call your hospice team or 911 right away.')}
                          </AlertDescription>
                        </Alert>
                        <ul className="space-y-3">
                          {selectedSymptom.call_immediately.map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-2" />
                              <span className="text-foreground font-medium">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary text-white">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="heading-2 text-white">{t('symptomGuide.needMoreGuidance', 'Need More Guidance?')}</h2>
            <p className="body-lg text-white/80">
              {t('symptomGuide.ctaDescription', 'Chat with our AI assistant for personalized guidance on managing symptoms and understanding what to expect.')}
            </p>
            <Link to="/chat" data-testid="symptom-cta-chat">
              <Button variant="secondary" className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg">
                <MessageCircle className="w-5 h-5 mr-2" />
                {t('symptomGuide.askAboutSymptoms', 'Ask About Symptoms')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
